/**
 * NormasPage — ruta pública /normas (S9-C7)
 *
 * Muestra todos los frameworks integrados con descripción + valor que aporta ComplianceOS.
 * Datos estáticos (ADR-S9-005): NO consume /templates ni /readiness (auth-only).
 * Copy en frameworkCatalog.js (ES/EN curado). Metadatos en frameworks.js.
 *
 * Honestidad (§5.2 del tech-design):
 * - Los 4 core IT + NORTIC A7/A8/A9 + 6 Zona Franca: catálogos integrados.
 * - nist_csf_2, iso_31000, iso_22301: destinos de cross-mapping, badge "Referencia".
 */
import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle2, ArrowRight, ExternalLink, Globe } from "lucide-react";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { useI18n } from "../i18n/I18nProvider";
import { FRAMEWORKS, getFrameworkLabel, getFrameworkColor } from "../constants/frameworks";
import { FRAMEWORK_CATALOG } from "../constants/frameworkCatalog";
import PublicNav from "../components/PublicNav";

// ============================================================
// Copy de chrome (títulos, botones, etiquetas de sección)
// El copy largo (description/value) vive en frameworkCatalog.js
// ============================================================
const CHROME = {
  en: {
    badge: "FRAMEWORKS & STANDARDS",
    h1: "All Frameworks Integrated in ComplianceOS",
    subtitle: "From IT compliance to Dominican government regulations and free trade zone requirements — all managed in one platform.",
    group_core: "Core IT Compliance",
    group_reference: "Cross-Mapping Reference Frameworks",
    group_reference_note: "These frameworks are used as cross-mapping targets to enrich your core IT compliance posture. They are not independently managed catalogs.",
    group_rdlatam: "Dominican Government — NORTIC",
    group_sectorial: "Regulated Sectors (Banking, Telecom)",
    group_zonafranca: "Zona Franca (Free Trade Zone)",
    badge_reference: "Reference",
    badge_integrated: "Integrated",
    badge_crossmap: "Cross-Mapping Target",
    value_title: "What ComplianceOS gives you",
    cta_section_title: "Ready to start your compliance journey?",
    cta_section_subtitle: "Book a personalized walkthrough tailored to your frameworks.",
    cta: "Request a Demo",
    cta_back: "← Back to Home",
    footer_rights: "© 2026 ComplianceOS by MSSPRD. All rights reserved.",
  },
  es: {
    badge: "NORMAS Y ESTÁNDARES",
    h1: "Todas las Normas Integradas en ComplianceOS",
    subtitle: "Desde cumplimiento IT hasta normativas del Gobierno Dominicano y requisitos de Zona Franca — todo gestionado en una sola plataforma.",
    group_core: "Cumplimiento IT Core",
    group_reference: "Marcos de Referencia (Cross-Mapping)",
    group_reference_note: "Estos marcos se usan como destinos de cross-mapping para enriquecer tu postura de cumplimiento IT. No son catálogos de controles gestionados de forma independiente.",
    group_rdlatam: "Gobierno Dominicano — NORTIC",
    group_sectorial: "Sectores Regulados (Banca, Telecom)",
    group_zonafranca: "Zona Franca",
    badge_reference: "Referencia",
    badge_integrated: "Integrado",
    badge_crossmap: "Destino Cross-Mapping",
    value_title: "Lo que ComplianceOS te da",
    cta_section_title: "¿Listo para comenzar tu camino al cumplimiento?",
    cta_section_subtitle: "Agenda una demostración personalizada adaptada a tus marcos.",
    cta: "Solicitar Demo",
    cta_back: "← Volver al inicio",
    footer_rights: "© 2026 ComplianceOS by MSSPRD. Todos los derechos reservados.",
  },
};

// Group ordering as defined in the tech-design §5.4
const GROUP_ORDER = ["core_it", "reference", "rd_latam", "sectorial", "zona_franca"];

// Map frameworks.js groups to display groups (splitting "core" into integrated vs reference)
const classifyFramework = (fw) => {
  const cat = FRAMEWORK_CATALOG[fw.id];
  if (fw.group === "core" && cat?.isReference) return "reference";
  if (fw.group === "core") return "core_it";
  if (fw.group === "rd_latam") return "rd_latam";
  if (fw.group === "sectorial") return "sectorial";
  if (fw.group === "zona_franca") return "zona_franca";
  return "core_it";
};

const FrameworkCard = ({ fw, lang }) => {
  const [expanded, setExpanded] = useState(false);
  const cat = FRAMEWORK_CATALOG[fw.id];
  const color = getFrameworkColor(fw.id);
  const isReference = cat?.isReference ?? false;
  const c = CHROME[lang];

  if (!cat) return null;

  return (
    <motion.article
      initial={{ opacity: 0, y: 16 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.4 }}
      className="border border-white/10 bg-[#0D122E] p-6 hover:border-white/20 transition group"
      data-testid={`normas-card-${fw.id}`}
    >
      {/* Header */}
      <div className="flex items-start justify-between gap-4 mb-4">
        <div className="flex items-start gap-3 min-w-0">
          {/* Color indicator */}
          <div
            className="w-3 h-3 rounded-full mt-1.5 shrink-0"
            style={{ backgroundColor: color }}
            aria-hidden="true"
          />
          <div className="min-w-0">
            <h3 className="font-heading font-bold text-base text-white leading-snug">
              {getFrameworkLabel(fw.id)}
            </h3>
            {fw.authority && (
              <p className="text-xs text-zinc-400 mt-0.5">{fw.authority} · {fw.edition}</p>
            )}
            {fw.category && (
              <p className="text-xs text-zinc-400 mt-0.5">{fw.category}</p>
            )}
          </div>
        </div>
        <Badge
          variant="outline"
          className={`shrink-0 text-[10px] font-semibold tracking-[0.12em] uppercase ${
            isReference
              ? "border-zinc-600 text-zinc-400 bg-zinc-800/40"
              : "border-primary/40 text-primary bg-primary/10"
          }`}
        >
          {isReference ? c.badge_crossmap : c.badge_integrated}
        </Badge>
      </div>

      {/* Description */}
      <p className="text-sm text-zinc-400 leading-relaxed mb-4">
        {cat.description[lang]}
      </p>

      {/* Value list — expandable on mobile, always visible on md+ */}
      <div>
        <button
          className="text-xs font-semibold text-primary hover:text-primary/80 transition mb-3 flex items-center gap-1 md:hidden"
          onClick={() => setExpanded(!expanded)}
          aria-expanded={expanded}
        >
          {c.value_title} {expanded ? "▲" : "▼"}
        </button>
        <div className={`${expanded ? "block" : "hidden"} md:block`}>
          <p className="text-xs font-semibold uppercase tracking-[0.12em] text-zinc-400 mb-2 hidden md:block">
            {c.value_title}
          </p>
          <ul className="space-y-1.5">
            {cat.value[lang].map((item, i) => (
              <li key={i} className="flex items-start gap-2 text-xs text-zinc-300">
                <CheckCircle2
                  className="w-3.5 h-3.5 mt-0.5 shrink-0 text-primary/70"
                  aria-hidden="true"
                />
                <span>{item}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </motion.article>
  );
};

const SectionLabel = ({ text }) => (
  <div className="flex items-center gap-3 mb-6">
    <div className="h-px flex-1 bg-white/10" />
    <span className="text-[10px] font-semibold uppercase tracking-[0.2em] text-zinc-400 shrink-0">
      {text}
    </span>
    <div className="h-px flex-1 bg-white/10" />
  </div>
);

const NormasPage = () => {
  const { locale, setLocale } = useI18n();
  const lang = locale === "es" ? "es" : "en";
  const c = CHROME[lang];
  const navigate = useNavigate();

  // Group frameworks by display category
  const grouped = {};
  GROUP_ORDER.forEach((g) => { grouped[g] = []; });
  FRAMEWORKS.forEach((fw) => {
    const group = classifyFramework(fw);
    if (grouped[group]) grouped[group].push(fw);
  });

  const fadeUp = {
    initial: { opacity: 0, y: 24 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true, margin: "-80px" },
    transition: { duration: 0.6, ease: "easeOut" },
  };

  return (
    <div className="min-h-screen bg-[#0A0F26] text-white overflow-hidden font-sans" data-testid="normas-page">
      <PublicNav scrolled />

      {/* Hero */}
      <section className="pt-28 pb-16 px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div {...fadeUp} transition={{ duration: 0.5 }}>
            <Badge
              variant="outline"
              className="border-primary/30 text-primary bg-primary/5 font-medium text-[10px] tracking-[0.2em] px-3 py-1 mb-6"
            >
              {c.badge}
            </Badge>
          </motion.div>
          <motion.h1
            {...fadeUp}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="text-3xl sm:text-4xl lg:text-5xl font-bold font-heading tracking-tight leading-[1.1] mb-4"
          >
            {c.h1}
          </motion.h1>
          <motion.p
            {...fadeUp}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="text-zinc-400 max-w-2xl mx-auto mb-6"
          >
            {c.subtitle}
          </motion.p>
          <motion.div {...fadeUp} transition={{ duration: 0.6, delay: 0.3 }}>
            <Link
              to="/"
              className="text-xs text-zinc-400 hover:text-zinc-300 transition inline-flex items-center gap-1"
              data-testid="normas-back-link"
            >
              {c.cta_back}
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Framework groups */}
      <main className="max-w-7xl mx-auto px-6 lg:px-8 pb-24 space-y-20">

        {/* Core IT */}
        <section aria-labelledby="section-core-it" data-testid="section-core-it">
          <motion.div {...fadeUp}>
            <SectionLabel text={c.group_core} />
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {grouped.core_it.map((fw) => (
              <FrameworkCard key={fw.id} fw={fw} lang={lang} />
            ))}
          </div>
        </section>

        {/* Reference frameworks */}
        <section aria-labelledby="section-reference" data-testid="section-reference">
          <motion.div {...fadeUp}>
            <SectionLabel text={c.group_reference} />
            <p className="text-sm text-zinc-400 mb-6 max-w-2xl">{c.group_reference_note}</p>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {grouped.reference.map((fw) => (
              <FrameworkCard key={fw.id} fw={fw} lang={lang} />
            ))}
          </div>
        </section>

        {/* NORTIC / RD Gov */}
        <section aria-labelledby="section-rd-latam" data-testid="section-rd-latam">
          <motion.div {...fadeUp}>
            <SectionLabel text={c.group_rdlatam} />
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {grouped.rd_latam.map((fw) => (
              <FrameworkCard key={fw.id} fw={fw} lang={lang} />
            ))}
          </div>
        </section>

        {/* Sectorial — verticales regulados RD (Banca, Telco) */}
        <section aria-labelledby="section-sectorial" data-testid="section-sectorial">
          <motion.div {...fadeUp}>
            <SectionLabel text={c.group_sectorial} />
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {grouped.sectorial.map((fw) => (
              <FrameworkCard key={fw.id} fw={fw} lang={lang} />
            ))}
          </div>
        </section>

        {/* Zona Franca */}
        <section aria-labelledby="section-zona-franca" data-testid="section-zona-franca">
          <motion.div {...fadeUp}>
            <SectionLabel text={c.group_zonafranca} />
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {grouped.zona_franca.map((fw) => (
              <FrameworkCard key={fw.id} fw={fw} lang={lang} />
            ))}
          </div>
        </section>
      </main>

      {/* Final CTA */}
      <section className="py-20 px-6 border-t border-white/10 bg-[#0D122E]" data-testid="normas-cta">
        <div className="max-w-2xl mx-auto text-center">
          <motion.div {...fadeUp}>
            <h2 className="text-2xl sm:text-3xl font-bold font-heading tracking-tight mb-3">
              {c.cta_section_title}
            </h2>
            <p className="text-zinc-400 mb-8">{c.cta_section_subtitle}</p>
            <Button
              size="lg"
              onClick={() => navigate("/demo")}
              className="bg-action text-action-foreground hover:bg-action/90 font-semibold h-12 px-8 group"
              data-testid="normas-cta-btn"
            >
              {c.cta}
              <ArrowRight className="w-4 h-4 ml-2 transition-transform group-hover:translate-x-1" />
            </Button>
          </motion.div>
        </div>
      </section>

      {/* Footer minimal */}
      <footer className="border-t border-white/10 py-8 px-6 bg-[#0A0F26]">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <Link to="/" className="flex items-center" aria-label="ComplianceOS Home">
            <img
              src="/brand/complianceos-logo-inverse.svg"
              alt="ComplianceOS by MSSPRD"
              className="h-7 w-auto"
              draggable={false}
            />
          </Link>
          <p className="text-xs text-zinc-600">{c.footer_rights}</p>
        </div>
      </footer>
    </div>
  );
};

export default NormasPage;
